/**
 * msgType → 行为单点映射（任务 2c 定义，9d 迁入共用层）。
 *
 * 原在 `src/worker/ws/messages.ts`；主线程视图模型（messageModel.ts）也要按
 * category 分流渲染（气泡/提示条/信令不渲染），为不破「主线程不带值导入
 * Worker 模块」约定迁到 src/chat/，worker 侧原名再导出、调用方零改动。
 *
 * 为什么要单点映射：旧版有 4 份 msgType 白名单（refresh / displayable / can-create /
 * preview）各自为政互相矛盾（贴纸 44 在刷新名单却不在可展示名单 → 收到完全不更新）。
 * 新版所有下游（store、IndexedDB、会话列表、聊天窗、未读计数、历史合并）一律从
 * `msgTypeSpec()` 取行为，不允许再各自 switch 数字。
 *
 * 权威来源：go_im `internal/pkg/chat/chat.go` 常量 + 各广播点（protocol.md §5.5）。
 */

// ---------------------------------------------------------------------------
// msgType 常量（与 go_im internal/pkg/chat/chat.go 逐一对应）
// ---------------------------------------------------------------------------

export const MsgType = {
  Text: 1,
  Image: 2,
  Card: 3, // 名片
  Voice: 4,
  Location: 5,
  Link: 6,
  Video: 7,
  File: 8,
  FriendApply: 9, // 加好友申请通知
  FriendAccepted: 10, // 加好友已接受
  SetNoDisturb: 11,
  SetMuted: 12, // 群内禁言成员
  SetUnmuted: 13,
  SetAllMuted: 14,
  SetAllUnmuted: 15,
  ReadReceipt: 16, // Msg=已读到的 msgId 字符串
  Pin: 17,
  Unpin: 18,
  FriendApplyRejected: 19,
  MsgDelete: 20, // Msg=被删 id 的 JSON 数组
  UnpinAll: 21,
  AllowPrivateChat: 22,
  NoPrivateChat: 23,
  GroupApplyNotice: 24, // 群主/管理员收到的入群申请
  GroupJoined: 25, // 自己/他人进群成功
  AdminAdded: 26,
  AdminRemoved: 27,
  MemberAdded: 28, // Ext={"joins":[{uid,nickname}]}
  MemberRemoved: 29,
  MemberLeft: 30,
  OwnerTransferred: 31,
  GroupProfileChanged: 32, // Msg 不含改了什么 → 应用层待下次打开重拉 profile
  SetAfterView: 33, // Msg={"after_view_time":秒}，含 0=关闭（set_after_view 推双方）
  // 后端常量名 TypeAfterViewDel 注释写「取消阅后即焚」，实际是 cron 焚烧删除通知：
  // Msg="[msgId]"（AutoDelAfterView 推双方，Id=0/FriendId=0 只有 ChatcontentId）。
  // 旧版误当「设置已关闭」处理且不删行（protocol §6.5 补注），新版按真实语义删行。
  AfterViewBurned: 34,
  FriendDeleted: 35,
  FriendOffline: 36, // 旧机制，被 presence 取代，忽略
  SessionDeleted: 37, // Msg={"content_id","user_id"}
  FriendOnline: 38, // 旧机制，被 presence 取代，忽略
  GroupDissolved: 39, // Msg={"group_id","group_name","user_id","action":"dissolve"}
  GroupCreated: 40,
  MsgEdited: 41, // Uuid=原消息uuid，Msg=新内容（裸文本），Edited=1
  MsgInserted: 42, // 后端当前未发送（fnc_msg.go 注释掉了），保留映射
  MsgHiddenEdited: 43, // 隐蔽改单：Id=原消息id，Msg=新内容，不标记已编辑
  Sticker: 44,
} as const;

export type MsgTypeValue = (typeof MsgType)[keyof typeof MsgType];

// ---------------------------------------------------------------------------
// msgType → 行为映射（单点定义）
// ---------------------------------------------------------------------------

/**
 * - content：时间线气泡消息（可渲染、可被引用/转发等——具体动作集由 UI 层再按类型细分）
 * - system-note：时间线居中提示条（文案由任务 14.5 按 MsgType 常量映射）
 * - signal：纯信令，不进时间线，只触发 store/DB mutation。
 *   历史拉取（msg/list）若返回 signal 类型的行，同样按信令应用、不渲染。
 */
export type MsgCategory = 'content' | 'system-note' | 'signal';

export interface MsgTypeSpec {
  readonly category: MsgCategory;
  /**
   * 服务端落库（不在 BlockedMsgTypes）→ 历史拉取可兜底。
   * false = 纯实时帧，错过推送即丢，客户端要么靠状态接口兜底、要么容忍。
   */
  readonly persisted: boolean;
  /** 计入会话未读角标（自己发的消息由调用方按 userId 排除） */
  readonly countsUnread: boolean;
  /** 更新会话预览文本并按时间 bump 排序（对齐服务端 last_msg 重算口径） */
  readonly bumpsConversation: boolean;
  /** 协议未收录的新类型：渲染占位气泡，提醒尽快补映射 */
  readonly unknown?: boolean;
}

const CONTENT: MsgTypeSpec = {
  category: 'content',
  persisted: true,
  countsUnread: true,
  bumpsConversation: true,
};
/**
 * 群事件提示条：进时间线、bump 会话，不计未读。
 * persisted=false 是 14.5 轮实锤：这批类型虽不在 BlockedMsgTypes，但后端所有
 * 广播点都以 Id=1 发（PublishMsgToGroup 不写任何存储），25 虽 Id=0 却走
 * PublishmsgtoroomWs 且 ChatModel=group（仅私聊落库）——历史 msg/list 永远
 * 不会出现，服务端 last_msg 也不含它们。客户端按临时注记处理：apply.ts 合成
 * 排序 id 推给打开中会话渲染，不写本地库（刷新即逝，旧版同口径）。
 */
const NOTE: MsgTypeSpec = {
  category: 'system-note',
  persisted: false,
  countsUnread: false,
  bumpsConversation: true,
};
const SIGNAL: MsgTypeSpec = {
  category: 'signal',
  persisted: false,
  countsUnread: false,
  bumpsConversation: false,
};
/** 落库的信令（历史里会出现，合并时照常按信令应用） */
const SIGNAL_PERSISTED: MsgTypeSpec = { ...SIGNAL, persisted: true };

const SPECS: Readonly<Record<MsgTypeValue, MsgTypeSpec>> = {
  [MsgType.Text]: CONTENT,
  [MsgType.Image]: CONTENT,
  [MsgType.Card]: CONTENT,
  [MsgType.Voice]: CONTENT,
  [MsgType.Location]: CONTENT,
  [MsgType.Link]: CONTENT,
  [MsgType.Video]: CONTENT,
  [MsgType.File]: CONTENT,
  [MsgType.FriendApply]: SIGNAL,
  [MsgType.FriendAccepted]: SIGNAL,
  [MsgType.SetNoDisturb]: SIGNAL,
  [MsgType.SetMuted]: SIGNAL,
  [MsgType.SetUnmuted]: SIGNAL,
  [MsgType.SetAllMuted]: SIGNAL,
  [MsgType.SetAllUnmuted]: SIGNAL,
  [MsgType.ReadReceipt]: SIGNAL,
  [MsgType.Pin]: SIGNAL,
  [MsgType.Unpin]: SIGNAL,
  [MsgType.FriendApplyRejected]: SIGNAL_PERSISTED,
  [MsgType.MsgDelete]: SIGNAL,
  [MsgType.UnpinAll]: SIGNAL,
  [MsgType.AllowPrivateChat]: NOTE,
  [MsgType.NoPrivateChat]: NOTE,
  [MsgType.GroupApplyNotice]: SIGNAL,
  [MsgType.GroupJoined]: NOTE,
  [MsgType.AdminAdded]: NOTE,
  [MsgType.AdminRemoved]: NOTE,
  [MsgType.MemberAdded]: NOTE,
  [MsgType.MemberRemoved]: NOTE,
  [MsgType.MemberLeft]: NOTE,
  [MsgType.OwnerTransferred]: NOTE,
  [MsgType.GroupProfileChanged]: NOTE,
  [MsgType.SetAfterView]: SIGNAL,
  [MsgType.AfterViewBurned]: SIGNAL,
  [MsgType.FriendDeleted]: SIGNAL,
  [MsgType.FriendOffline]: SIGNAL,
  [MsgType.SessionDeleted]: SIGNAL_PERSISTED,
  [MsgType.FriendOnline]: SIGNAL,
  [MsgType.GroupDissolved]: SIGNAL_PERSISTED,
  [MsgType.GroupCreated]: SIGNAL,
  [MsgType.MsgEdited]: SIGNAL_PERSISTED,
  [MsgType.MsgInserted]: CONTENT,
  [MsgType.MsgHiddenEdited]: SIGNAL,
  [MsgType.Sticker]: CONTENT,
};

/**
 * 未知类型按内容消息处理（占位气泡 + 计未读 + bump）：未来服务端新增内容类型时
 * 优雅降级为「有一条不支持的消息」，而不是像旧版贴纸 44 那样静默丢失。
 * 代价是未来新增的信令类型会短暂显示占位噪音——可见、可立即补映射。
 */
const UNKNOWN: MsgTypeSpec = { ...CONTENT, unknown: true };

export function msgTypeSpec(msgType: number): MsgTypeSpec {
  return SPECS[msgType as MsgTypeValue] ?? UNKNOWN;
}
